import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  fetchWikiTree,
  fetchWikiTemplates,
  createWikiPage,
  saveWikiPageContent,
} from '../api/client'
import { useAuth } from '../context/AuthContext'
import Breadcrumbs from '../components/Breadcrumbs'
import type { WikiTreeBook, WikiTreeShelf, WikiTemplate } from '../types/wiki'
import { FileText, Plus, X, Loader2, AlertCircle } from 'lucide-react'
import BananaSlug from 'github-slugger'

function slugifyTitle(title: string): string {
  return new BananaSlug().slug(title)
}

export default function BookPage() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const { role } = useAuth()
  const [book, setBook] = useState<WikiTreeBook | null>(null)
  const [shelf, setShelf] = useState<WikiTreeShelf | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [templates, setTemplates] = useState<WikiTemplate[]>([])
  const [title, setTitle] = useState('')
  const [pageSlug, setPageSlug] = useState('')
  const [chapterSlug, setChapterSlug] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [creating, setCreating] = useState(false)
  const [createErr, setCreateErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    void fetchWikiTree()
      .then((tree) => {
        if (cancelled) return
        for (const s of tree.shelves) {
          const b = s.books.find((bk) => bk.slug === slug)
          if (b) {
            setShelf(s)
            setBook(b)
            return
          }
        }
        setErr('Book not found')
      })
      .catch(() => {
        if (!cancelled) setErr('Wiki not available')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const openDialog = useCallback(async () => {
    setDialogOpen(true)
    setTitle('')
    setPageSlug('')
    setChapterSlug(book?.chapters[0]?.slug ?? '')
    setTemplateId('')
    setCreateErr(null)
    try {
      const { templates: items } = await fetchWikiTemplates()
      setTemplates(items)
    } catch {
      setTemplates([])
    }
  }, [book])

  const handleTitleChange = (value: string) => {
    setTitle(value)
    if (!pageSlug || pageSlug === slugifyTitle(title)) {
      setPageSlug(slugifyTitle(value))
    }
  }

  const handleCreate = async () => {
    if (!book || !chapterSlug || !title.trim() || !pageSlug.trim()) {
      setCreateErr('Title, slug, and chapter are required.')
      return
    }
    const password = window.prompt('Enter your password to create this page:')
    if (password === null) return

    const template = templates.find((t) => t.id === templateId)
    const prefix = template?.corpus_path_prefix ?? 'wiki'
    const corpus_path = `${prefix}/${book.slug}/${pageSlug}.md`

    setCreating(true)
    setCreateErr(null)
    try {
      await createWikiPage({
        chapter_slug: chapterSlug,
        title: title.trim(),
        slug: pageSlug.trim(),
        corpus_path,
        published: false,
      })
      if (template?.markdown) {
        await saveWikiPageContent(pageSlug.trim(), { markdown: template.markdown, password })
      }
      setDialogOpen(false)
      navigate(`/p/${pageSlug.trim()}`)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Create failed'
          : 'Create failed'
      setCreateErr(msg)
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading book…
      </div>
    )
  }

  if (err || !book || !shelf) {
    return (
      <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 m-4">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span>{err || 'Book not found'}</span>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: shelf.name, to: `/s/${shelf.slug}` },
          { label: book.name },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold text-gray-100">{book.name}</h1>
        {role === 'admin' && (
          <button
            type="button"
            onClick={() => void openDialog()}
            className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60"
          >
            <Plus className="w-4 h-4" />
            New page
          </button>
        )}
      </div>

      {book.chapters.map((chapter) => (
        <section key={chapter.id} className="mb-8">
          <h2 className="text-lg font-semibold text-gray-300 mb-3">{chapter.name}</h2>
          {chapter.pages.length === 0 && (
            <p className="text-sm text-gray-500 border border-dashed border-gray-800 rounded-lg px-4 py-6 text-center">
              No pages in this chapter yet.
            </p>
          )}
          <ul className="space-y-2">
            {chapter.pages.map((page) => (
              <li key={page.id}>
                <Link
                  to={`/p/${page.slug}`}
                  className={`flex items-center gap-2 py-2 px-3 rounded-lg border border-gray-800/60 hover:border-primary-700/40 hover:bg-gray-900/50 min-h-[44px] ${
                    !page.published ? 'opacity-60 italic' : ''
                  }`}
                >
                  <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="text-gray-200">{page.title}</span>
                  {!page.published && (
                    <span className="text-xs text-amber-500/90 ml-auto">draft</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {dialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="presentation"
          onClick={() => setDialogOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-gray-700 bg-gray-950 shadow-xl p-5"
            role="dialog"
            aria-label="New wiki page"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">New page</h2>
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {createErr && <p className="text-error-400 text-sm mb-3">{createErr}</p>}

            <div className="space-y-3">
              <div>
                <label htmlFor="new-page-title" className="block text-sm text-gray-400 mb-1">
                  Title
                </label>
                <input
                  id="new-page-title"
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 min-h-[44px] text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="new-page-slug" className="block text-sm text-gray-400 mb-1">
                  Slug
                </label>
                <input
                  id="new-page-slug"
                  type="text"
                  value={pageSlug}
                  onChange={(e) => setPageSlug(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 min-h-[44px] text-gray-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="new-page-chapter" className="block text-sm text-gray-400 mb-1">
                  Chapter
                </label>
                <select
                  id="new-page-chapter"
                  value={chapterSlug}
                  onChange={(e) => setChapterSlug(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 min-h-[44px] text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {book.chapters.map((ch) => (
                    <option key={ch.id} value={ch.slug}>
                      {ch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="new-page-template" className="block text-sm text-gray-400 mb-1">
                  Template
                </label>
                <select
                  id="new-page-template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 min-h-[44px] text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Blank page</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="min-h-[44px] px-4 rounded-lg text-sm font-medium border border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={creating}
                onClick={() => void handleCreate()}
                className="min-h-[44px] px-4 rounded-lg text-sm font-medium bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create page'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
